import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import backend from '../services/backend';
import { TableSkeleton } from '../components/Skeleton';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useServerFeatures } from '../hooks/useServerFeatures';
import { ClusterOutliersCard } from './categoryView/ClusterOutliersCard';
import { CategoryNeighborhoodCard } from './categoryView/CategoryNeighborhoodCard';
import { Header } from './categoryView/Header';
import { PagesTable } from './categoryView/PagesTable';
import { CategoryPageChart } from './categoryView/CategoryPageChart';
import { PagePreviews } from './categoryView/PagePreviews';
import { CategoryDetails } from './categoryView/CategoryDetails';
import { useCategoryActions } from './categoryView/useCategoryActions';
import { DeleteCategoryDialog } from './categoryView/DeleteCategoryDialog';

const SEARCH_DEBOUNCE_MS = 350;

export default function CategoryView() {
  const { setTitle } = useDocumentTitle('Category View');
  const { id } = useParams<{ id: string }>();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);

  useEffect(() => {
    // Clearing the input must filter instantly — only keystrokes are debounced.
    if (search === '') {
      setDebouncedSearch('');
      return;
    }
    const t = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search]);

  const categoryQuery = useQuery({
    queryKey: ['getCategory', id],
    queryFn: () => backend.getCategory(id!).then((res) => res.data),
    enabled: !!id,
  });

  const actions = useCategoryActions({
    categoryId: id,
    pageIds: (categoryQuery.data?.pages ?? []).map((page) => page.id),
    onActionComplete: () => categoryQuery.refetch(),
  });

  useEffect(() => {
    if (categoryQuery.data?.categoryDetails.name) {
      setTitle(categoryQuery.data.categoryDetails.name + ' - Category View');
    }
  }, [categoryQuery.data?.categoryDetails.name]);

  const filteredPages = useMemo(() => {
    const searchLower = debouncedSearch.toLowerCase();
    return (categoryQuery.data?.pages ?? []).filter((page) => {
      const label = page.classification?.[0]?.[0] || '';
      const id = page.id || '';
      return id.toLowerCase().includes(searchLower) || label.toLowerCase().includes(searchLower);
    });
  }, [categoryQuery.data?.pages, debouncedSearch]);

  // The Page Previews card scrolls internally. scrollIntoView walks every scrollable ancestor,
  // so it scrolls the card's inner container to the cluster AND the window to the card (so the
  // gesture still works when the card sits below the fold) — and it lets the browser render and
  // correct the content-visibility:auto intrinsic-size estimates, which a manual scrollTo can't.
  const previewsContainerRef = useRef<HTMLDivElement>(null);
  // Cancels a flash still waiting on scroll-settle from the previous click (null when none pending).
  const pendingFlashRef = useRef<(() => void) | null>(null);
  const scrollToCluster = useCallback((clusterId: number) => {
    const container = previewsContainerRef.current;
    const section = container?.querySelector<HTMLElement>(
      `[data-cluster-id="${CSS.escape(String(clusterId))}"]`,
    );
    if (!container || !section) return;

    // Drop any flash queued by an earlier click so only the latest cluster lights up.
    pendingFlashRef.current?.();
    section.scrollIntoView({ behavior: 'smooth', block: 'start' });

    // Flash each page card in the cluster so the scrolled-to target stands out for a few
    // seconds. Removing the class, forcing a reflow, then re-adding restarts the animation on
    // repeat clicks of the same cluster; animationend self-cleans.
    const flash = () => {
      section.querySelectorAll<HTMLElement>('[data-page-card]').forEach((card) => {
        card.classList.remove('cluster-flash');
        void card.offsetWidth;
        card.classList.add('cluster-flash');
        card.addEventListener('animationend', () => card.classList.remove('cluster-flash'), { once: true });
      });
    };

    // Start the flash only once the smooth scroll settles, so the pulse isn't spent while the
    // target is still off-screen on a long scroll. `scrollend` fires when scrolling stops; the
    // timeout covers the no-scroll case (already in view) and browsers without scrollend.
    const cancel = () => {
      clearTimeout(fallback);
      container.removeEventListener('scrollend', start);
      pendingFlashRef.current = null;
    };
    const start = () => {
      cancel();
      flash();
    };
    const fallback = setTimeout(start, 600);
    container.addEventListener('scrollend', start);
    pendingFlashRef.current = cancel;
  }, []);

  // Cancel a still-pending flash if the page unmounts before the scroll settles.
  useEffect(() => () => pendingFlashRef.current?.(), []);

  const loading = categoryQuery.isLoading;
  const error = categoryQuery.error as Error | null;
  const response = categoryQuery.data;
  const features = useServerFeatures();
  const trainingEnabled = features?.training === true;

  if (loading && !response) {
    return (
      <div className="">
        <Header actions={actions} trainingEnabled={trainingEnabled} />
        <TableSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="">
        <div className="bg-white dark:bg-zinc-800 dark:border dark:border-zinc-700 dark:shadow-zinc-950/30 shadow rounded-lg p-6">
          <div className="text-center text-red-600">{error.message}</div>
        </div>
      </div>
    );
  }

  const details = response?.categoryDetails;

  return (
    <div className="">
      <Header
        actions={actions}
        trainingEnabled={trainingEnabled}
        onDelete={details && id ? () => setDeleteOpen(true) : undefined}
      />

      {details && id && (
        <DeleteCategoryDialog
          open={deleteOpen}
          onClose={() => setDeleteOpen(false)}
          categoryId={id}
          name={details.name}
          linkedPageCount={details.linkedPageCount}
        />
      )}

      <div className="flex flex-col gap-8">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Left Column - Category Details */}
          <div className="space-y-6 lg:col-span-2">
            {response && <CategoryDetails response={response} />}
          </div>
          <div className="lg:col-span-3 flex flex-col gap-4">
            {response && <CategoryPageChart response={response} />}

            {id && (
              <ClusterOutliersCard
                key={categoryQuery.data?.categoryDetails.embeddingClustersCount ?? 'loading'}
                categoryId={id}
                onSelectCluster={scrollToCluster}
              />
            )}
          </div>
        </div>

        {id && <CategoryNeighborhoodCard categoryId={id} onSelectCluster={scrollToCluster} />}

        <PagesTable pages={filteredPages} search={search} setSearch={setSearch} />

        <PagePreviews
          pages={filteredPages}
          category={response?.categoryDetails.name}
          scrollContainerRef={previewsContainerRef}
        />
      </div>
    </div>
  );
}
